import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { toSmartTitleCase } from "../../lib/formatters";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const TAGS = ["Lead", "Active Buyer", "Distributor", "Manufacturer"];
const TITLES = ["Mr.", "Ms.", "Mrs.", "Dr."];

export default function ContactForm({ open, onClose, initial, prefill, onSave, fields, products, users, currentUser }) {
  const emptyRow = () => ({ _key: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${Math.random()}`), product_name: "", product_code: "", volume: "" });
  const [f, setF] = useState({
    company_name: "", title: "", contact_person: "", designation: "", email: "", phone: "",
    website: "", address: "", city: "", state: "", zip_code: "",
    products: [emptyRow()], remarks: "", tags: [], custom_fields: {}
  });
  const [newOwnerId, setNewOwnerId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (initial) {
      // Migrate legacy singular fields to array on read
      let rows = Array.isArray(initial.products) && initial.products.length ? initial.products : null;
      if (!rows) {
        rows = (initial.product_name || initial.product_code)
          ? [{ ...emptyRow(), product_name: initial.product_name || "", product_code: initial.product_code || "", volume: initial.volume || "" }]
          : [emptyRow()];
      } else {
        // Ensure every row has a volume key so the input stays controlled,
        // and give each a stable _key so React tracks rows correctly across
        // add / remove operations.
        rows = rows.map((r, i) => ({
          _key: r._key || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${i}`),
          product_name: r.product_name || "",
          product_code: r.product_code || "",
          volume: r.volume || (i === 0 ? (initial.volume || "") : ""),
        }));
      }
      setF((prev) => ({ ...prev, ...initial, products: rows }));
      setNewOwnerId(initial.created_by || "");
    } else {
      setF({
        company_name: "", title: "", contact_person: "", designation: "", email: "", phone: "",
        website: "", address: "", city: "", state: "", zip_code: "",
        products: [emptyRow()], remarks: "", tags: [], custom_fields: {}
      });
      setNewOwnerId("");
      if (prefill) {
        // Grab A Deal pre-fills what the manager already knows; it is still a
        // brand-new contact, so the form stays in create mode.
        setF((prev) => ({
          ...prev, ...prefill,
          products: prefill.products?.length
            ? prefill.products.map((r, i) => ({ ...emptyRow(), ...r, _key: `pf_${i}` }))
            : [emptyRow()],
        }));
      }
    }
    /* eslint-disable-next-line */
  }, [initial, prefill, open]);

  const isManager = currentUser?.role === "manager";
  const canReassign = isManager && !!initial;
  // Owners must be people who can actually work the account.
  const activeUsers = useMemo(
    () => (users || []).filter((u) => u.active !== false && u.role !== "unit_head"), [users]);

  const updateProduct = (idx, patch) => {
    const rows = f.products.map((r, i) => i === idx ? { ...r, ...patch } : r);
    setF({ ...f, products: rows });
  };
  const changeProductName = (idx, name) => {
    const prod = products.find((p) => p.name === name);
    const currentCode = f.products[idx].product_code;
    const stillValid = prod?.codes?.some((c) => c.code === currentCode);
    updateProduct(idx, { product_name: name, product_code: stillValid ? currentCode : "" });
  };
  const addProductRow = () => setF({ ...f, products: [...f.products, emptyRow()] });
  const removeProductRow = (idx) => {
    if (f.products.length <= 1) return;
    setF({ ...f, products: f.products.filter((_, i) => i !== idx) });
  };
  const codesFor = (name) => (products.find((p) => p.name === name)?.codes) || [];

  const toggleTag = (t) => setF({ ...f, tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t] });

  const submittingRef = React.useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return; // same-tick double click
    // Client-side required-field validation
    const missing = [];
    if (!f.company_name.trim()) missing.push("Company Name");
    if (!f.title.trim()) missing.push("Title");
    if (!f.contact_person.trim()) missing.push("Contact Person");
    if (!f.designation.trim()) missing.push("Designation");
    if (!f.email.trim()) missing.push("Email");
    if (!f.phone.trim()) missing.push("Phone");
    if (!f.website.trim()) missing.push("Website");
    // Product rows — need at least one, and each row needs both name+code
    const activeRows = (f.products || []).filter((r) => (r.product_name || "").trim() || (r.product_code || "").trim());
    if (activeRows.length === 0) {
      missing.push("Product Name"); missing.push("Product Code");
    } else {
      activeRows.forEach((r, i) => {
        if (!(r.product_name || "").trim()) missing.push(`Product Name (row ${i + 1})`);
        if (!(r.product_code || "").trim()) missing.push(`Product Code (row ${i + 1})`);
      });
    }
    if (!f.tags || f.tags.length === 0) missing.push("Tags");
    if (missing.length) {
      toast.error(`Please fill in the required field(s): ${missing.join(", ")}`);
      return;
    }
    // Email format check (after presence has passed)
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (f.email && !emailRe.test(f.email.trim())) {
      toast.error(`Invalid email format: "${f.email.trim()}" — please enter a valid email like name@example.com`);
      return;
    }
    try {
      setSaving(true);
      submittingRef.current = true;
      const res = initial
        ? await api.put(`/contacts/${initial.id}`, f)
        : await api.post("/contacts", f);
      // Ownership reassignment (Manager only, editing an existing contact)
      if (canReassign && newOwnerId && newOwnerId !== initial.created_by) {
        const r = await api.patch(`/contacts/${initial.id}/owner`, { new_owner_id: newOwnerId });
        const n = r.data?.deals_reassigned || 0;
        toast.success(`Owner reassigned to ${r.data?.new_owner?.name}${n ? ` — ${n} related deal${n === 1 ? "" : "s"} also transferred` : ""}`);
      } else {
        toast.success(initial ? "Contact updated" : res.data?.duplicate_of ? "Contact already created" : "Contact created");
      }
      onSave(res.data); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    submittingRef.current = false;
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit Contact" : "New Contact"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {canReassign && (
            <div className="col-span-2 bg-primary/5 border border-primary/20 rounded-md p-3">
              <Label className="text-primary font-semibold">Owner (Sales Staff assigned)</Label>
              <select data-testid="contact-owner" value={newOwnerId}
                onChange={(e) => setNewOwnerId(e.target.value)}
                className="w-full mt-1.5 h-10 rounded-md border border-input bg-white px-3 text-sm">
                {activeUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} {u.role === "manager" ? " (Manager)" : ""}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1.5">
                As Manager, you can transfer this contact to a different team member. All related deals in the pipeline will be reassigned automatically.
              </p>
            </div>
          )}
          <div className="col-span-2"><Label>Company Name <span className="text-destructive">*</span></Label><Input data-testid="contact-company" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, company_name: toSmartTitleCase(e.target.value) }))} /></div>
          {fields.map((cf) => (
            <div key={cf.id} className="col-span-2"><Label>{cf.label}</Label>
              <Input type={cf.type === "number" ? "number" : cf.type === "date" ? "date" : "text"}
                data-testid={`contact-cf-${cf.name}`}
                value={f.custom_fields[cf.name] || ""}
                onChange={(e) => setF({ ...f, custom_fields: { ...f.custom_fields, [cf.name]: e.target.value } })}
                onBlur={cf.type === "text" || !cf.type
                  ? (e) => setF((s) => ({ ...s, custom_fields: { ...s.custom_fields, [cf.name]: toSmartTitleCase(e.target.value) } }))
                  : undefined} />
            </div>
          ))}
          <div>
            <Label>Title <span className="text-destructive">*</span></Label>
            <select data-testid="contact-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value="">—</option>
              {TITLES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div><Label>Contact Person <span className="text-destructive">*</span></Label><Input data-testid="contact-person" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, contact_person: toSmartTitleCase(e.target.value) }))} /></div>
          <div><Label>Designation <span className="text-destructive">*</span></Label><Input data-testid="contact-designation" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, designation: toSmartTitleCase(e.target.value) }))} /></div>
          <div><Label>Email <span className="text-destructive">*</span></Label><Input type="email" data-testid="contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Phone <span className="text-destructive">*</span></Label><Input data-testid="contact-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><Label>Website <span className="text-destructive">*</span></Label><Input data-testid="contact-website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} /></div>
          <div className="col-span-2"><Label>Address</Label><Input data-testid="contact-address" placeholder="Street address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, address: toSmartTitleCase(e.target.value) }))} /></div>
          <div><Label>City</Label><Input data-testid="contact-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, city: toSmartTitleCase(e.target.value) }))} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>State</Label><Input data-testid="contact-state" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, state: toSmartTitleCase(e.target.value) }))} /></div>
            <div><Label>Zip Code</Label><Input data-testid="contact-zip" value={f.zip_code} onChange={(e) => setF({ ...f, zip_code: e.target.value })} /></div>
          </div>
          <div className="col-span-2 border border-border rounded-md p-3 bg-secondary/30">
            <div className="flex items-center justify-between mb-2">
              <Label className="mb-0">Products Purchased <span className="text-destructive">*</span></Label>
              <span className="text-xs text-muted-foreground">{f.products.length} row{f.products.length === 1 ? "" : "s"}</span>
            </div>
            <div className="space-y-2" data-testid="contact-products-list">
              {f.products.map((row, idx) => (
                <div key={row._key || idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-start">
                  <div>
                    {idx === 0 && <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Product Name</div>}
                    <select data-testid={`contact-product-${idx}`} value={row.product_name}
                      onChange={(e) => changeProductName(idx, e.target.value)}
                      className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                      <option value="">— Select a product —</option>
                      {products.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                      {row.product_name && !products.some((p) => p.name === row.product_name) && (
                        <option value={row.product_name}>{row.product_name} (legacy)</option>
                      )}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Product Code</div>}
                    <select data-testid={`contact-product-code-${idx}`} value={row.product_code || ""}
                      onChange={(e) => updateProduct(idx, { product_code: e.target.value })}
                      disabled={!row.product_name}
                      className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm disabled:bg-neutral-100 disabled:cursor-not-allowed">
                      <option value="">
                        {!row.product_name ? "— Pick product first —" : (codesFor(row.product_name).length === 0 ? "— No codes —" : "— Select a code —")}
                      </option>
                      {codesFor(row.product_name).map((c) => <option key={c.id} value={c.code}>{c.code}</option>)}
                      {row.product_code && !codesFor(row.product_name).some((c) => c.code === row.product_code) && (
                        <option value={row.product_code}>{row.product_code} (legacy)</option>
                      )}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Volume</div>}
                    <Input data-testid={`contact-product-volume-${idx}`}
                      value={row.volume || ""}
                      onChange={(e) => updateProduct(idx, { volume: e.target.value })}
                      placeholder="e.g. 500 kg/month"
                      className="h-10" />
                  </div>
                  <div className={idx === 0 ? "pt-[22px]" : ""}>
                    <Button type="button" variant="ghost" size="sm"
                      data-testid={`remove-product-${idx}`}
                      disabled={f.products.length <= 1}
                      onClick={() => removeProductRow(idx)}
                      title={f.products.length <= 1 ? "At least one product is required" : "Remove this row"}
                      className="h-10 w-10 p-0 text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addProductRow}
              data-testid="add-product-row"
              className="mt-3 border-dashed border-primary/40 text-primary hover:bg-primary/5">
              <Plus className="w-3.5 h-3.5 mr-1" />Add another product
            </Button>
          </div>
          <div className="col-span-2">
            <Label>Tags <span className="text-destructive">*</span></Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="contact-tags">
              {TAGS.map((t) => (
                <button type="button" key={t} onClick={() => toggleTag(t)}
                  data-testid={`tag-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.tags.includes(t) ? "bg-primary text-white border-primary" : "bg-white border-border text-neutral-700"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2"><Label>Remarks</Label><Textarea rows={3} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, remarks: toSmartTitleCase(e.target.value) }))} /></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="contact-save" disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
